// Reconciling a Drip position against the share IOU that backs it.
//
// A deposit is recorded twice and nothing on-chain keeps the two in step: the
// transferable share token (holding it IS the claim) and the user_position PDA.
// Burning or transferring the token never touches the position, so a position
// can book shares that no longer back a claim — and `redeem` burns the token,
// so for those shares it can never succeed.
//
// All values are raw on-chain integers (1e9-scaled). BigInt is mandatory: a
// full position exceeds Number.MAX_SAFE_INTEGER, where a Number-based compare
// picks the wrong side of the clamp. The es2015 target forbids BigInt literals,
// so use BigInt(n).

const ZERO = BigInt(0);

/**
 * Shares the position books: active and pending alike.
 *
 * Pending shares are withdrawable immediately. `last_deposit_epoch` drives the
 * off-chain reward schedule — when a deposit starts earning, not when it can
 * leave — and `redeem` never consults it.
 */
export const bookedSharesRaw = (
  activeSharesRaw: bigint,
  pendingSharesRaw: bigint
): bigint => activeSharesRaw + pendingSharesRaw;

/**
 * What the wallet can actually redeem.
 *
 * Clamped in both directions. Down because a burned or transferred-away token
 * leaves nothing for `redeem` to burn. Up because a token received by transfer
 * is not redeemable either: the recipient's own position PDA books zero, and
 * the program pays against the position, not against the token.
 */
export const withdrawableSharesRaw = (booked: bigint, held: bigint): bigint =>
  booked < held ? booked : held;

/**
 * True when the IOU no longer covers what the position books. Permanent — no
 * retry recovers it — so it must stay distinguishable from an unreadable read.
 */
export const isClaimShort = (booked: bigint, held: bigint): boolean =>
  booked > ZERO && held < booked;
